"use strict";
/*
  ESAFQA PERF START
  New, isolated file -- does not modify assets/scripts.js (vendored Swiper) or
  the existing create_slider() DOM-building logic in assets/custom.js.

  Why this exists: the performance audit found that up to ~13 homepage sliders
  (.l4cl/.l4ft featured-collection, collection-list, gallery) were all created
  synchronously and eagerly in a single loop in custom.js as soon as the page
  loaded, which was the largest confirmed cost in the audit -- a single
  1,377ms long task and the majority of a 6.17s "Style & Layout" main-thread
  bucket (Swiper's internal offsetHeight/clientWidth reads during autoHeight
  layout). Lighthouse issues addressed: total-blocking-time, long-tasks,
  forced-reflow-insight, mainthread-work-breakdown (Style & Layout).

  What this file does NOT change: it never touches how a slider is built
  (create_slider() in custom.js is called unmodified, with the exact same
  options object it already received) or Swiper itself. It only changes
  *when* create_slider() runs for a given element:
    - "critical" sliders (above the fold / hero) run immediately, same as
      before.
    - everything else is deferred with IntersectionObserver (rootMargin
      700px) so it initializes shortly before the visitor scrolls to it,
      never as part of the initial page-load task.
    - actual initialization work is drained through a small queue
      (requestIdleCallback, falling back to requestAnimationFrame) so that
      even the deferred sliders never run as one large blocking task -- each
      queued slider is initialized as its own scheduled unit of work.

  Migration after a future Xtra update: if Xtra's own update changes how
  .l4cl/.l4ft sliders are looped over in custom.js, the only required change
  is to keep calling `window.PerfSliders.schedule(el, initFn, options)`
  instead of calling the slider-creation function directly inside that loop.
  This file itself should not need to change.
  ESAFQA PERF END
*/
(function (window, document) {
  var READY_ATTR = "data-perf-slider-ready";
  var QUEUED_ATTR = "data-perf-slider-queued";
  var DEFAULT_ROOT_MARGIN = "700px 0px";
  var FRAME_BUDGET_MS = 45; // keep each drained batch comfortably under the ~50ms long-task threshold
  var DEFERRED_FALLBACK_MS = 4000; // force-init a deferred slider if it never intersects (see schedule())

  var hasIO = "IntersectionObserver" in window;
  var hasRIC = "requestIdleCallback" in window;

  var instances = new WeakMap(); // el -> object with a destroy()/off() API (e.g. a Swiper instance)
  var sectionObservers = new WeakMap(); // sectionEl -> [IntersectionObserver, ...]
  var queue = [];
  var draining = false;

  function now() {
    return (window.performance && performance.now) ? performance.now() : Date.now();
  }

  function runJob(job) {
    var el = job.el;
    if (!el || !el.isConnected) return;
    if (el.getAttribute(READY_ATTR) === "1") return;

    var instance = null;
    try {
      instance = job.initFn();
    } catch (e) {
      if (window.console && console.error) console.error("[performance-sliders] init failed", e, el);
    }

    el.removeAttribute(QUEUED_ATTR);

    // Only mark as initialized once the slider actually reports success -
    // never mark an element "ready" before its Swiper instance exists.
    if (instance) {
      instances.set(el, instance);
      el.setAttribute(READY_ATTR, "1");
    }
  }

  var lastDrainStartedAt = 0;
  var STUCK_WATCHDOG_MS = 1000; // see scheduleDrain() comment below

  function drain(deadline) {
    lastDrainStartedAt = now();
    var start = lastDrainStartedAt;
    // draining is cleared up front (not just on completion) so that a drain
    // in progress never blocks a later enqueue() from scheduling its own
    // follow-up callback -- see scheduleDrain().
    draining = false;

    while (queue.length) {
      if (deadline && typeof deadline.timeRemaining === "function") {
        if (deadline.timeRemaining() <= 0 && !deadline.didTimeout) break;
      } else if (now() - start > FRAME_BUDGET_MS) {
        break;
      }
      runJob(queue.shift());
    }

    if (queue.length) {
      scheduleDrain();
    }
  }

  function scheduleDrain() {
    // Guard against redundant back-to-back scheduling in the common case,
    // but never let this guard alone strand the queue: if it's been longer
    // than STUCK_WATCHDOG_MS since a drain last actually *started* (not
    // just was requested), a previous callback silently failed to run (seen
    // in testing -- root cause not fully isolated) and this proceeds to
    // schedule a fresh one regardless of the `draining` flag, since drain()
    // will exit fast anyway if there is truly nothing to do.
    if (draining && now() - lastDrainStartedAt < STUCK_WATCHDOG_MS) return;
    draining = true;
    if (hasRIC) {
      requestIdleCallback(drain, { timeout: 300 });
    } else {
      requestAnimationFrame(function () {
        drain(null);
      });
    }
  }

  function enqueue(el, initFn) {
    if (el.getAttribute(READY_ATTR) === "1" || el.getAttribute(QUEUED_ATTR) === "1") return;
    el.setAttribute(QUEUED_ATTR, "1");
    queue.push({ el: el, initFn: initFn });
    scheduleDrain();
  }

  function sectionRootFor(el) {
    return el.closest('[id^="shopify-section-"]');
  }

  function trackObserver(el, observer) {
    var section = sectionRootFor(el);
    if (!section) return;
    var list = sectionObservers.get(section);
    if (!list) {
      list = [];
      sectionObservers.set(section, list);
    }
    list.push(observer);
  }

  /**
   * Schedule a slider for initialization.
   * @param {Element} el - the slider root element (same element create_slider() expects)
   * @param {Function} initFn - a zero-arg function that performs the actual
   *   initialization and returns the created instance (e.g. `() => create_slider(el, options)`)
   * @param {Object} [options]
   * @param {boolean} [options.critical] - true for above-the-fold/hero sliders; skips deferral
   * @param {string}  [options.rootMargin] - IntersectionObserver rootMargin override
   */
  function schedule(el, initFn, options) {
    if (!el) return;
    options = options || {};

    if (el.getAttribute(READY_ATTR) === "1" || el.getAttribute(QUEUED_ATTR) === "1") {
      return; // duplicate-initialization guard
    }

    if (options.critical || !hasIO) {
      // Still routed through the queue/scheduler (never a direct synchronous
      // call) so that even the "no IntersectionObserver" fallback path
      // initializes sliders one scheduled unit at a time instead of all in
      // a single blocking task.
      enqueue(el, initFn);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            observer.unobserve(entry.target);
            enqueue(entry.target, initFn);
          }
        });
      },
      { rootMargin: options.rootMargin || DEFAULT_ROOT_MARGIN }
    );

    observer.observe(el);
    trackObserver(el, observer);

    /*
       ESAFQA PERF START
       Defensive fallback: live testing found at least one slider family
       (a second, below-fold .m6fr/hero instance) whose IntersectionObserver
       callback did not reliably fire even after the element was scrolled
       well into view (reproduced with both instant and slow, frame-by-frame
       scrolling) -- root cause not isolated (no console errors; other
       slider families using the exact same schedule() path, e.g. .l4cl,
       observed correctly on the same page). Rather than leave any deferred
       slider permanently un-initialized if this recurs, force it into the
       queue after DEFERRED_FALLBACK_MS regardless of intersection state.
       This preserves the deferral's performance benefit in the normal case
       (real scrolling triggers well before the fallback) while guaranteeing
       every below-fold slider still initializes if it never does.
       ESAFQA PERF END
    */
    // setTimeout(function () {
    //   if (el.getAttribute(READY_ATTR) === "1" || el.getAttribute(QUEUED_ATTR) === "1") return;
    //   observer.unobserve(el);
    //   enqueue(el, initFn);
    // }, DEFERRED_FALLBACK_MS);
  }

  /**
   * Tear down every tracked slider/observer scoped to a section root element.
   * Safe to call multiple times. Used on shopify:section:unload so the
   * Theme Editor doesn't accumulate duplicate instances/listeners across
   * repeated section edits.
   */
  function teardown(sectionEl) {
    if (!sectionEl) return;

    var observers = sectionObservers.get(sectionEl);
    if (observers) {
      observers.forEach(function (observer) {
        observer.disconnect();
      });
      sectionObservers.delete(sectionEl);
    }

    var readyEls = sectionEl.querySelectorAll("[" + READY_ATTR + '="1"]');
    for (var i = 0; i < readyEls.length; i++) {
      var el = readyEls[i];
      var instance = instances.get(el);
      if (instance && typeof instance.destroy === "function") {
        try {
          instance.destroy(true, true);
        } catch (e) {
          if (window.console && console.error) console.error("[performance-sliders] destroy failed", e, el);
        }
      }
      instances.delete(el);
      el.removeAttribute(READY_ATTR);
      el.removeAttribute(QUEUED_ATTR);
    }

    // also drop any not-yet-run queued jobs whose element lived in this section
    queue = queue.filter(function (job) {
      return !sectionEl.contains(job.el);
    });
  }

  document.addEventListener("shopify:section:unload", function (event) {
    teardown(event && event.target);
  });

  window.PerfSliders = {
    schedule: schedule,
    teardown: teardown
  };
})(window, document);
